import axios from 'axios'

export type FavoriteType = 'merchant' | 'product' | 'basket'

export interface Favorite {
  favorite_id: string
  user_id: string
  type: FavoriteType
  item_id: string
  created_at: string
  item_name: string | null
  item_description: string | null
  item_image: string | null
}

export interface AddFavoriteDto {
  user_id: string
  type: FavoriteType
  item_id: string
}

export interface FavoriteFilters {
  user_id: string
  type?: FavoriteType
}

export interface FavoriteCheck {
  is_favorited: boolean
  favorite: Favorite | null
}

export interface FavoriteCount {
  type: FavoriteType
  item_id: string
  favorite_count: number
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const favoriteApi = axios.create({
  baseURL: `${API_BASE_URL}/favorites`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
favoriteApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
favoriteApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const favoriteService = {
  // Get all favorites for a user
  async getFavorites(filters: FavoriteFilters): Promise<{ success: boolean; data: Favorite[]; total: number }> {
    const params = new URLSearchParams()
    params.append('user_id', filters.user_id)
    
    if (filters.type) params.append('type', filters.type)

    const response = await favoriteApi.get('', { params })
    return response.data
  },

  // Add item to favorites
  async addFavorite(favoriteData: AddFavoriteDto): Promise<{ success: boolean; data: Favorite; message: string }> {
    const response = await favoriteApi.post('', favoriteData)
    return response.data
  },

  // Remove item from favorites by ID
  async removeFavoriteById(favoriteId: string): Promise<{ success: boolean; message: string }> {
    const response = await favoriteApi.delete(`/${favoriteId}`)
    return response.data
  },

  // Remove item from favorites by user_id, type, and item_id
  async removeFavorite(user_id: string, type: FavoriteType, item_id: string): Promise<{ success: boolean; message: string }> {
    const params = new URLSearchParams()
    params.append('user_id', user_id)
    params.append('type', type)
    params.append('item_id', item_id)

    const response = await favoriteApi.delete('', { params })
    return response.data
  },

  // Check if item is favorited
  async checkFavorite(user_id: string, type: FavoriteType, item_id: string): Promise<{ success: boolean; data: FavoriteCheck }> {
    const params = new URLSearchParams()
    params.append('user_id', user_id)
    params.append('type', type)
    params.append('item_id', item_id)

    const response = await favoriteApi.get('/check', { params })
    return response.data
  },

  // Get favorite count for an item
  async getFavoriteCount(type: FavoriteType, item_id: string): Promise<{ success: boolean; data: FavoriteCount }> {
    const params = new URLSearchParams()
    params.append('type', type)
    params.append('item_id', item_id)

    const response = await favoriteApi.get('/count', { params })
    return response.data
  },

  // Get user's favorite statistics
  async getUserFavoriteStats(userId: string): Promise<{
    success: boolean
    data: {
      total_favorites: number
      by_type: Record<FavoriteType, number>
      recent_favorites: Favorite[]
    }
  }> {
    try {
      const allFavorites = await this.getFavorites({ user_id: userId })
      const favorites = allFavorites.data
      
      const by_type: Record<FavoriteType, number> = {
        merchant: 0,
        product: 0,
        basket: 0
      }
      
      favorites.forEach(favorite => {
        by_type[favorite.type] = (by_type[favorite.type] || 0) + 1
      })
      
      const recent_favorites = favorites
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10)
      
      return {
        success: true,
        data: {
          total_favorites: favorites.length,
          by_type,
          recent_favorites
        }
      }
    } catch (error) {
      return {
        success: true,
        data: {
          total_favorites: 0,
          by_type: { merchant: 0, product: 0, basket: 0 },
          recent_favorites: []
        }
      }
    }
  },

  // Get popular favorites across all users
  async getPopularFavorites(limit: number = 10): Promise<{
    success: boolean
    data: Array<{
      type: FavoriteType
      item_id: string
      item_name: string
      favorite_count: number
      item_image: string | null
    }>
  }> {
    // This would call a popular favorites endpoint if implemented
    // For now, return mock data
    const mockPopularFavorites = [
      {
        type: 'merchant' as FavoriteType,
        item_id: 'merch_123',
        item_name: 'Organic Market',
        favorite_count: 156,
        item_image: 'https://example.com/images/merchant1.jpg'
      },
      {
        type: 'product' as FavoriteType,
        item_id: 'prod_456',
        item_name: 'Fresh Vegetables Basket',
        favorite_count: 89,
        item_image: 'https://example.com/images/product1.jpg'
      },
      {
        type: 'basket' as FavoriteType,
        item_id: 'basket_789',
        item_name: 'Weekly Family Basket',
        favorite_count: 67,
        item_image: 'https://example.com/images/basket1.jpg'
      },
      {
        type: 'merchant' as FavoriteType,
        item_id: 'merch_456',
        item_name: 'Local Farm',
        favorite_count: 45,
        item_image: 'https://example.com/images/merchant2.jpg'
      }
    ]

    return {
      success: true,
      data: mockPopularFavorites.slice(0, limit)
    }
  }
}

export default favoriteService
